<!-- filepath: untitled:Untitled-4 -->
<template>
  <div class="summary-section" :data-aos="isInternalView ? '' : 'fade-up'" data-aos-once="true">
    <v-container>
      <v-expansion-panels v-model="panel" dark accordion multiple>
        <v-expansion-panel expand>
          <v-expansion-panel-header>
            <v-app-bar dark color="#212121">
              <v-toolbar-title style="text-align:center;">Youtube Post Analysis Summary</v-toolbar-title>
              <v-spacer></v-spacer>
              
              <v-menu
                :close-on-content-click="false"
                bottom
                left
                v-if="showRequiredActions()"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openColSettingsModal"
                  >
                    <i class="fas fa-cog fa-lg" data-toggle="tooltip" data-placement="top" title="Settings"></i>
                  </v-btn>
                </template>
              </v-menu>
              <v-btn
                v-if="showRequiredActions()"
                icon
                color="primary"
                @click="downloadSummary"
              >
                <i class="fas fa-download fa-lg" data-toggle="tooltip" data-placement="top" title="Download"></i>
              </v-btn>
              <v-btn
                v-if="isInternalView && showRequiredActions()"
                icon
                color="primary"
                @click.stop="$emit('open-analysis')"
              >
                <i class="fas fa-magic fa-lg" data-toggle="tooltip" data-placement="top" title="AI Analysis"></i>
              </v-btn>
            </v-app-bar>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card id="youtube-analysis-summary" class="mx-auto" outlined color="#000000">
              <v-card-text>
                <div>
                  <!-- Group Filter Buttons (right aligned, no "All" button) -->
                  <div v-if="groups.length > 0" class="group-filter-btns-right">
                    <div class="group-btns">
                      <v-btn
                        v-for="group in groups"
                        :key="group.groupID"
                        :color="selectedGroup === group.groupID ? 'orange' : '#fff'"
                        :class="['group-btn', { 'active-btn': selectedGroup === group.groupID }]"
                        @click="selectedGroup = selectedGroup === group.groupID ? null : group.groupID"
                        depressed
                        small
                        style="margin-right:8px; color: #212121;"
                      >
                        {{ group.groupName || group.groupID }}
                      </v-btn>
                    </div>
                  </div>
                  <div class="text-right mb-2">
                    <!-- Save and Cancel buttons for drag-and-drop reordering -->
                    <v-btn
                      @click.native.stop
                      @click="cancelPostOrder"
                      color="warning"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      Cancel
                    </v-btn>

                    <v-btn
                      @click.native.stop
                      @click="savePostOrder"
                      color="success"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      <v-icon small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                  </div>
                  <v-data-table
                    dark
                    :headers="computedHeaders"
                    :items="filteredPosts"
                    :items-per-page="-1"
                    item-key="key"
                    class="elevation-15 report-summary-table"
                    :key="dataTableKey"
                    v-sortable-data-table
                    @sorted="handleDataTableDragDrop"
                    :custom-sort="customSort"
                  >
                    <template v-for="(h, index) in computedHeaders" v-slot:[`header.${h.value}`]="{ header }">
                      <legend-icon :key="index" :header="h" />
                    </template>
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-icon
                          small
                          class="move-cursor"
                        >
                          mdi-arrow-all
                        </v-icon>
                        <v-btn
                          v-if="isInternalView"
                          class="btn-reload"
                          text
                          x-small
                          color="warning"
                          @click="refreshReportInf(item.id)"
                        >
                          <v-icon dark small>
                            mdi-reload
                          </v-icon>
                        </v-btn>
                        <a class="caption" style="color:#fff" :href="'https://youtube.com/' + item.name" target="_blank">@{{item.name}}</a>
                      </div>
                    </template>
                    <template v-slot:[`item.url`]="{ item }">
                      <td class="text-left">
                        <v-btn
                          x-small
                          text
                          color="secondary"
                          elevation="0"
                          :href="item.url"
                          :target="'_blank'"
                        >
                          View
                        </v-btn>
                      </td>
                    </template>
                    <template v-slot:item.outreach="{ item }">
                      {{item.outreach === 0 || !item.outreach ? '-' : item.outreach | thousandSeprator}}
                    </template>
                    <template v-slot:item.views="{ item }">
                      {{item.views === 0 ? '-' : item.views | thousandSeprator}}
                    </template>
                    <template v-slot:item.likes="{ item }">
                      {{item.likes === 0 ? '-' : item.likes | thousandSeprator}}
                    </template>
                    <template v-slot:item.comments="{ item }">
                      {{item.comments === 0 ? '-' : item.comments | thousandSeprator}}
                    </template>
                    <template v-slot:item.new_engagement_rate="{ item }">
                      {{item.new_engagement_rate === null ? '-' : item.new_engagement_rate + '%'}}
                    </template>
                    <template v-slot:item.new_engagement_rate_view="{ item }">
                      {{item.new_engagement_rate_view === null ? '-' : item.new_engagement_rate_view + '%'}}
                    </template>
                    <template v-slot:item.engagement_rate_sd="{ item }">
                      {{item.engagement_rate_sd === null ? '-' : item.engagement_rate_sd + '%'}}
                    </template>
                    <template v-slot:item.median_engagement_rate="{ item }">
                      {{item.median_engagement_rate === null ? '-' : item.median_engagement_rate + '%'}}
                    </template>
                    <template slot="body.append">
                      <tr style="font-weight:500;">
                        <td><strong>TOTAL</strong></td>
                        <template v-for="header in computedHeaders">
                          <td :key="header.value" v-if="header.value !== 'name'">
                            <strong>
                              <template v-if="header.value === 'live_date'">-</template>
                              <template v-else-if="header.value === 'url'">-</template>
                              <template v-else-if="header.value === 'outreach'">{{ totalOutreach === 0 ? '-' : totalOutreach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'views'">{{ totalViews === 0 ? '-' : totalViews | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'likes'">{{ totalLikes === 0 ? '-' : totalLikes | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'comments'">{{ totalComments === 0 ? '-' : totalComments | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'interactions'">{{ totalInteractions === 0 ? '-' : totalInteractions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'avg_views'">-</template>
                              <template v-else-if="header.value === 'avg_likes'">-</template>
                              <template v-else-if="header.value === 'avg_comments'">-</template>
                              <template v-else-if="header.value === 'new_engagement_rate'">{{ totalEngagementRate === 0 ? '-' : totalEngagementRate }} %</template>
                              <template v-else-if="header.value === 'new_engagement_rate_view'">{{ totalEngagementRateView === 0 ? '-' : totalEngagementRateView }} %</template>
                              <template v-else-if="header.value === 'avg_engagement_rate'"> - </template>
                              <template v-else-if="header.value === 'median_engagement_rate'"> - </template>
                            </strong>
                          </td>
                        </template>
                      </tr>
                    </template>
                  </v-data-table>
                </div>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <table-cols-show-hide-modal
      v-model="colSettingsDialog"
      title="Youtube Post Analysis Summary"
      :items="tableColsItems"
      @updated="updateYoutubeAnalysisStatus"
      :isSort="true"
    />
  </div>
</template>

<style scoped>
.group-filter-btns-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}
.group-btns {
  display: flex;
  align-items: center;
}
.group-btn {
  min-width: 48px;
  font-weight: 500;
  background: #fff !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #212121 !important;
  transition: background 0.2s, color 0.2s;
}
.active-btn {
  background: orange !important;
  color: #fff !important;
}
</style>

<script>
/* eslint-disable */
import html2canvas from 'html2canvas';
import LegendSection from './Legend'
import LegendIcon from './LegendIcon'
import TableColsShowHideModal from '@/components/scoped/custom/TableColsShowHideModal'
import campaignHelper from '../views/Campaign/campaignHelper';
import config from '@/config';
import campaignAPIs from '../views/Campaign/campaignAPIs';
export default {
  name: 'rpt_tiktok_summary',
  props: ['posts', 'campaign', 'defaultCols', 'isInternalView'],
  components: {
    LegendSection,
    LegendIcon,
    TableColsShowHideModal
  },
  data () {
    return {
      page: 1,
      sortBy: 'live_date',
      sortDesc: true,
      dataTableKey: 0,
      disableSaveArrange: true,
      lineUpItems: [],
      panel: [0, 1],
      tikTokLegends: [],
      selectedGroup: null,
      colSettingsDialog: false,
      tableColsItems: [],
      editPostOrder: false,
      originalPosts: [],
      showHideFields: [
        { name: 'liveDate', label: 'Live Date' },
        { name: 'url', label: 'Post URL' },
        { name: 'showViews', label: 'Total Views' },
        { name: 'likes', label: 'Likes' },
        { name: 'comments', label: 'Comments' },
        { name: 'showInteractions', label: 'Interactions' },
        { name: 'avgViews', label: 'Average Views' },
        { name: 'avgLikes', label: 'Average Likes' },
        { name: 'avgComments', label: 'Average Comments' },
        { name: 'showEngagement', label: 'Engagement Rate by Interactions' },
        { name: 'showEngagementView', label: 'Engagement Rate by Views' },
        { name: 'showAvgEngagement', label: 'Average Engagement Rate' },
        { name: 'showMediaEngagement', label: 'Median Engagement Rate' }
      ],
      headers: [
        { text: 'Influencer', align: 'start', sortable: false, value: 'name' },
        { text: 'Live Date', value: 'live_date', legendText: 'Live Date' },
        { text: 'Post URL', value: 'url', legendText: 'Post URL' },
        { text: 'Audience Size', value: 'outreach' },
        { text: 'Total Views', value: 'views', legendText: 'Number of views' },
        { text: 'Likes', value: 'likes', legendText: 'Number of likes' },
        { text: 'Comments', value: 'comments', legendText: 'Number of comments' },
        { text: 'Interactions', value: 'interactions', legendText: 'The sum of likes, comments, saves, shares' },
        { text: 'Average Views', value: 'avg_views', legendText: 'Average views over the last 30 posts' },
        { text: 'Average Likes', value: 'avg_likes', legendText: 'Average likes over the last 30 posts' },
        { text: 'Average Comments', value: 'avg_comments', legendText: 'Average comments over the last 30 posts' },
        { text: 'Engagement Rate by Interactions', value: 'new_engagement_rate', legendText: 'The total number of interactions divided by the subscriber numbers' },
        { text: 'Engagement Rate by Views', value: 'new_engagement_rate_view', legendText: 'The total number of views divided by the subscriber numbers' },
        { text: 'Average Engagement Rate', value: 'engagement_rate_sd', legendText: 'Average engagement for all advertorials and organic content' },
        { text: 'Median Engagement Rate', value: 'median_engagement_rate', legendText: 'Distribution by engagement rate for influencers having followers in the similar range/tier' }
      ],
      rightSection: [
        { title: 'Likes', description: 'Number of likes' },
        { title: 'Comments', description: 'Number of comments' },
        { title: 'Average Views', description: 'Average views over the last 30 posts' },
        { title: 'Average Likes', description: 'Average likes over the last 30 posts' },
        { title: 'Average Comments', description: 'Average comments over the last 30 posts' },
        { title: 'Live Date', description: 'Live Date' },
        { title: 'Post URL', description: 'Post URL' },
        { title: 'Engagement Rate by Interactions', description: 'Followers divided by total interactions for this campaign' },
        { title: 'Average Engagement Rate', description: 'Average engagement for all advertorials and organic content' },
        { title: 'Median Engagement Rate', description: 'Distrubution by engagement rate for influencers having followers in the similar range/tier' }
      ]
    }
  },
  computed: {
    sortedPosts() {
      return this.posts
    },
    currentUserRole: function() {
      return Number(this.$store.state.user.role);
    },
    groups() {
      const groupMap = new Map();
      this.sortedPosts.forEach(post => {
        if (post.groupID && post.groupName) {
          groupMap.set(post.groupID, post.groupName);
        }
        if (Array.isArray(post.reports)) {
          post.reports.forEach(report => {
            if (report.groupID && report.groupName) {
              groupMap.set(report.groupID, report.groupName);
            }
          });
        }
      });
      return Array.from(groupMap, ([groupID, groupName]) => ({
        groupID,
        groupName
      }));
    },
    filteredPosts() {
      if (!this.selectedGroup) return this.posts;
      return this.posts.filter(post => {
        if (post.groupID === this.selectedGroup) return true;
        if (Array.isArray(post.reports)) {
          return post.reports.some(report => report.groupID === this.selectedGroup);
        }
        return false;
      });
    },
    computedHeaders() {
      const status = this.campaign.youtubeAnalysisStatus || {};
      const finalHeaders = [];

      // 1. Add the "Influencer" column first (always fixed)
      const nameHeader = this.headers.find(h => h.value === 'name');
      if (nameHeader) {
        finalHeaders.push(nameHeader);
      }

      // 2. Iterate through the keys of youtubeAnalysisStatus to determine order and visibility
      for (const statusKey in status) {
        if (status[statusKey]) {
          const headerKey = statusKey.replace('show', '').toLowerCase();
          let foundHeader;

          if (headerKey === 'engagement') {
            foundHeader = this.headers.find(h => h.value === 'new_engagement_rate');
          } else if (headerKey === 'engagementview') {
            foundHeader = this.headers.find(h => h.value === 'new_engagement_rate_view');
          } else if (headerKey === 'avgengagement') {
            foundHeader = this.headers.find(h => h.value === 'engagement_rate_sd');
          } else if (headerKey === 'mediaengagement') {
            foundHeader = this.headers.find(h => h.value === 'median_engagement_rate');
          } else if (headerKey === 'livedate') {
            foundHeader = this.headers.find(h => h.value === 'live_date');
          } else {
            foundHeader = this.headers.find(h => h.value.toLowerCase() === headerKey);
          }
          
          if (foundHeader) {
            finalHeaders.push(foundHeader);
          }
        }
      }
      return finalHeaders;
    },
    totalOutreach() {
      let outreach = 0;
      this.filteredPosts.map(item => {
        outreach += item.outreach ? Number(item.outreach) : 0
      })
      return outreach
    },
    totalViews() {
      let views = 0;
      this.filteredPosts.map(item => {
        views += item.views ? Number(item.views) : 0
      })
      return views
    },
    totalLikes() {
      let likes = 0;
      this.filteredPosts.map(item => {
        likes += item.likes ? Number(item.likes) : 0
      })
      return likes
    },
    totalComments() {
      let comments = 0;
      this.filteredPosts.map(item => {
        comments += item.comments ? Number(item.comments) : 0
      })
      return comments
    },
    totalInteractions() {
      let interactions = 0;
      this.filteredPosts.map(item => {
        interactions += item.interactions ? Number(item.interactions) : 0
      })
      return interactions
    },
    totalEngagementRate() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.new_engagement_rate ? parseFloat(item.new_engagement_rate) : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalEngagementRateView() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.new_engagement_rate_view ? parseFloat(item.new_engagement_rate_view) : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalAvgViews() {
      let avgViews = 0;
      this.filteredPosts.map(item => {
        avgViews += item.avg_views ? Number(item.avg_views) : 0
      })
      return avgViews
    },
    totalAvgLikes() {
      let avgLikes = 0;
      this.filteredPosts.map(item => {
        avgLikes += item.avg_likes ? Number(item.avg_likes) : 0
      })
      return avgLikes
    },
    totalAvgComments() {
      let avgComments = 0;
      this.filteredPosts.map(item => {
        avgComments += item.avg_comments ? Number(item.avg_comments) : 0
      })
      return avgComments
    }
  },
  mounted() {},
  methods: {
    customSort: function(items, index, isDesc) {
      items.sort((a, b) => {
        if (!(isNaN(a[index[0]]))) {
          if (!isDesc[0]) {
            return (a[index[0]] - b[index[0]]);
          } else {
            return (b[index[0]] - a[index[0]]);
          }
        } else {
          if (!isDesc[0]) {
            return (a[index[0]] < b[index[0]]) ? -1 : 1;
          } else {
            return (b[index[0]] < a[index[0]]) ? -1 : 1;
          }
        }
      })
      this.lineUpItems = Object.assign([], items)
      this.disableSaveArrange = false
      return items;
    },
    handleDataTableDragDrop(event, items) {
      // check and save drag n drop update for table items/ grouped items
      if (items.length === 0 || !this.showRequiredActions()) {
        return;
      }
      
      if (!this.editPostOrder) {
        this.originalPosts = JSON.parse(JSON.stringify(this.posts));
      }
      
      const itemsPerPage = 15;
      const oldIndex = ((this.page - 1) * itemsPerPage) + event.oldIndex;
      const newIndex = ((this.page - 1) * itemsPerPage) + event.newIndex;
      let tmpPosts = []
      let groupID = items[0].groupID
      if (groupID) {
        let group = this.sortedPosts.find(p => p.id === groupID)
        if (group) {
          tmpPosts = group.reports
        }
      } else {
        tmpPosts = Object.assign([], this.sortedPosts)
      }
      const movedItem = tmpPosts.splice(oldIndex, 1)[0]
      tmpPosts.splice(newIndex, 0, movedItem)
      if (groupID) {
        this.posts = this.sortedPosts.map(p => {
          if (p.id === groupID) {
            p.reports = tmpPosts.map(tmp => tmp)
          }
          return p
        })
      } else {
        this.posts = tmpPosts.map(tmp => tmp)
      }
      this.lineUpItems = []
      this.lineUpItems = Object.assign([], tmpPosts)
      this.disableSaveArrange = false
      this.editPostOrder = true
    },
    savePostOrder() {
      this.updateLineUp();
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    cancelPostOrder() {
      this.posts = JSON.parse(JSON.stringify(this.originalPosts));
      this.lineUpItems = [];
      this.disableSaveArrange = true;
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    async updateLineUp() {
      await this.lineUpItems.map((item, index) => {
        let data = {}
        if (item.reportGroup) {
          data.groupID = item.id
          data.lineUpIndex = index
          data.posts = item.reports.map((element, rindex) => {
            return Object.assign({}, { id: element.id, lineUpIndex: rindex })
          })
        } else {
          data = {
            reportID: item.id,
            campaignID: item.campaignID,
            lineUpIndex: index
          }
        }
        campaignAPIs.updateLineUpIndex(data)
      })
    },
    async updateYoutubeAnalysisStatus(items) {
      let columns = this.campaign.youtubeAnalysisStatus || {}
      items.map(item => {
        columns[item.key] = item.value
      })
      let data = [Object.assign({}, {
        id: this.campaign.id,
        youtubeAnalysisStatus: columns
      })]
      await this.$store.dispatch('campaign/updateCampaignByIds', data)
      this.colSettingsDialog = false
    },
    showRequiredActions: function() {
      if (!this.$store.state.token || this.$store.state.token === 'null' || !this.$store.state.user) {
        return false
      } else {
        return config.userRoles.CLIENT !== this.currentUserRole;
      }
    },
    openColSettingsModal() {
      this.tableColsItems = []
      let tmpStatuses = {}
      this.showHideFields.map(header => {
        tmpStatuses[header.name] = true
      })
      tmpStatuses = Object.assign({}, tmpStatuses, this.campaign.youtubeAnalysisStatus)
      for (const [key, value] of Object.entries(tmpStatuses)) {
        let header = this.showHideFields.find(item => item.name === key)
        if (header) {
          this.tableColsItems.push({
            title: header.label,
            key,
            value
          })
        }
      }
      this.colSettingsDialog = true
    },
    downloadSummary() {
      const element = document.getElementById('youtube-analysis-summary');
      html2canvas(element).then(canvas => {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = 'youtube-analysis-summary.png';
        link.click();
      });
    },
    refreshReportInf(id) {
      this.$emit('refreshReportInf', id)
    }
  }
}
</script>



// WEBPACK FOOTER //
// YoutubeSummary.vue?5cd7b0ef